<template>
    <div class="contents">
        <NuxtErrorBoundary @error="handleCsrError">
            <NuxtClientFallback @ssr-error="handleSsrError">
                <Content v-if="canShowContent" :model="model" :api-key="apiKey" :content="builderContent"
                    :customComponents="loadedComponents" />
            </NuxtClientFallback>

            <template #error="{ error }">
                <div class="flex justify-center w-full">
                    <div class="w-fit m-2 px-4 py-10 rounded-md outline outline-1 outline-gray-300">
                        <div class="grid grid-flow-col grid-rows-2 gap-4">
                            <div class="row-span-2 content-center">
                                <Icon pack-prefix="far" icon="x" size="2x" width="2em"></Icon>
                            </div>
                            <h1 class="col-span-2 text-xl">An error occurred</h1>
                            <p class="col-span-2 content-center">
                                Sorry, this content can't be loaded right now. ({{ error }})
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </NuxtErrorBoundary>
    </div>
</template>

<script setup lang="ts">
import { type NuxtError } from "#app";
import {loadedComponents} from "~/composables/builderIoComponents"
import { Content, isPreviewing } from "@builder.io/sdk-vue";
import { NuxtClientFallback, NuxtErrorBoundary } from "#components";
import Icon from "./ui/atom/icon.vue";

interface BuilderContentProps {
    model: string;
    urlPath?: string | undefined; // By default the current page route will be used.  Set this to override that
}

interface BuilderContentEmits {
    (e: 'onLoad', data?: BuilderIOData<unknown>);
    (e: 'onError', error?: NuxtError<unknown>);
}

const emit = defineEmits<BuilderContentEmits>();
const props = defineProps<BuilderContentProps>();
const apiKey = builderIoService().getPublicApiKey();
const route = useRoute();

// make a call to builder for the provided model
const {
    data: builderContent,
    status: builderContentStatus,
    error: builderContentError,
    refresh: refreshBuilderContent
} = await useAsyncData(`builder-page-${route.path}`, () => builderIoService().fetchOneModel(props.model, props.urlPath ?? route.path));
const canShowContent = computed<boolean>(() => builderContent.value != null || isPreviewing());

const refresh = async () => {
    await refreshBuilderContent();
}

const handleSsrError = (error) => {
    //console.warn("Received error during SSR for builder content", error);
}

const handleCsrError = (error) => {
    //console.warn("Received error during CSR for builder content", error);
}

watchEffect(() => {
    if (builderContentStatus.value === 'success') {
        emit('onLoad', builderContent.value ?? undefined);
    }
    else if (builderContentStatus.value === 'error') {
        emit('onError', builderContentError.value ?? undefined);
    }
    else if (['idle', 'pending'].includes(builderContentStatus.value)) {
        console.debug(`Builder content model data status is currently ${builderContentStatus.value}`);
    }
});

</script>